import React, { useEffect, useMemo, useState } from 'react';
import SingleSelect, { type SingleSelectOption } from '../SingleSelect';
import printingService, { type Printer, type PrinterType } from '../../services/printing.service';

interface PrinterSelectProps {
  type: PrinterType;
  /** installname, '' = none */
  value: string;
  onChange: (installname: string) => void;
  disabled?: boolean;
  showClearOption?: boolean;
  ariaLabel?: string;
  size?: 'sm' | 'md';
  /** Called once the printer list has loaded (so callers can validate a preselection). */
  onLoaded?: (printers: Printer[]) => void;
}

/**
 * Active cms.printers of one type as a searchable dropdown. Display name is
 * what people call the printer ("BAYPORT_MFP"); the install name is the CUPS
 * queue the server actually spools to, shown alongside so the two can be matched
 * against the Table Maintenance > Printers page.
 */
const PrinterSelect: React.FC<PrinterSelectProps> = ({
  type, value, onChange, disabled, showClearOption, ariaLabel = 'Printer', size = 'md', onLoaded,
}) => {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    printingService.listPrinters(type)
      .then((rows) => { if (alive) { setPrinters(rows); onLoaded?.(rows); } })
      .catch(() => { if (alive) setPrinters([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // onLoaded is a notification, not an input; re-fetch only when the type changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const options = useMemo<SingleSelectOption[]>(
    () => printers.map((p) => ({
      value: p.installname,
      label: p.displayname === p.installname ? p.displayname : `${p.displayname} (${p.installname})`,
    })),
    [printers],
  );

  return (
    <SingleSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={loading ? 'Loading printers…' : 'Choose a printer…'}
      disabled={disabled}
      loading={loading}
      showClearOption={showClearOption}
      clearOptionLabel="No default"
      enableSearch
      searchPlaceholder="Search printers…"
      usePortal
      ariaLabel={ariaLabel}
      size={size}
    />
  );
};

export default PrinterSelect;
